import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';

/**
 * SocketIOProvider — custom Y.js provider that uses Socket.IO for transport.
 *
 * Connects the Yjs document to the server via an existing socket connection,
 * handles document sync, binary updates, awareness (cursor) state, and
 * user presence events.
 */
export class SocketIOProvider {
  constructor(socket, roomName, doc) {
    this.socket = socket;
    this.roomName = roomName;
    this.doc = doc;
    this.awareness = new Awareness(doc);
    this.status = 'connecting';
    this.callbacks = {
      status: [],
      synced: [],
      update: [],
    };
    this.synced = false;
    this.serverHadState = false;

    // Handler references for cleanup in destroy()
    this._docUpdateHandler = null;
    this._awarenessUpdateHandler = null;
    this._socketHandlers = {};

    this.initialize();
  }

  get connected() {
    return this.status === 'connected';
  }

  getDocument() {
    return this.doc;
  }

  on(name, callback) {
    if (!this.callbacks[name]) {
      this.callbacks[name] = [];
    }

    if (name === 'update') {
      // For collaboration cursor updates, format data in expected structure
      const wrappedCallback = () => {
        const states = this._getAwarenessStates();
        callback({ states, clientID: this.doc.clientID });
      };
      this.callbacks[name].push(wrappedCallback);
      // Initial call for existing cursors
      setTimeout(wrappedCallback, 0);
    } else {
      this.callbacks[name].push(callback);
    }

    return this;
  }

  off(event, callback) {
    if (this.callbacks[event]) {
      this.callbacks[event] = this.callbacks[event].filter(cb => cb !== callback);
    }
    return this;
  }

  emit(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event].forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error(`Error in callback for event ${event}:`, err);
        }
      });
    }
  }

  /**
   * Map a userId string to a stable numeric clientId for awareness.
   */
  getConsistentUserId(userId) {
    if (!userId) return this.doc.clientID;
    if (typeof userId === 'number') return userId;
    const numericPart = userId.replace(/[^0-9]/g, '');
    if (numericPart && numericPart.length > 0) {
      return parseInt(numericPart, 10);
    }
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  }

  _getAwarenessStates() {
    const states = {};
    this.awareness.getStates().forEach((state, clientId) => {
      if (state && state.user) {
        states[clientId] = {
          name: state.user.name,
          color: state.user.color,
          id: state.user.id,
          selection: state.selection,
        };
      }
    });
    return states;
  }

  cleanupDuplicateAwarenessStates() {
    try {
      const states = this.awareness.getStates();
      const userIds = new Map();
      const duplicates = [];

      states.forEach((state, clientId) => {
        if (state.user && state.user.id) {
          const userId = state.user.id;
          if (userIds.has(userId)) {
            duplicates.push(clientId);
          } else {
            userIds.set(userId, clientId);
          }
        }
      });

      if (duplicates.length > 0) {
        duplicates.forEach(clientId => {
          this.awareness.states.delete(clientId);
        });
        this._triggerAwarenessCallbacks();
      }
    } catch (err) {
      console.error('Error in awareness cleanup:', err);
    }
  }

  _triggerAwarenessCallbacks() {
    if (this.callbacks['update']) {
      const states = this._getAwarenessStates();
      this.callbacks['update'].forEach(callback => {
        try {
          callback({
            added: [],
            updated: [],
            removed: [],
            states,
            clientID: this.doc.clientID,
          });
        } catch (err) {
          console.error('Error in update callback:', err);
        }
      });
    }
  }

  initialize() {
    // ── Socket event handlers (stored for cleanup) ──────────────────────────

    this._socketHandlers.connect = () => {
      this.setStatus('connected');
      this.socket.emit('client_ready', {
        token: this.socket.auth.token,
        contract_id: this.roomName,
        color: this.socket.auth.color,
      });
    };
    this.socket.on('connect', this._socketHandlers.connect);

    this._socketHandlers.disconnect = () => {
      this.setStatus('disconnected');
    };
    this.socket.on('disconnect', this._socketHandlers.disconnect);

    this._socketHandlers.connect_error = (error) => {
      console.error('Socket connection error:', error);
      this.setStatus('error');
    };
    this.socket.on('connect_error', this._socketHandlers.connect_error);

    // Initial document state from server
    this._socketHandlers.sync = (data) => {
      // New protocol: { updates: [[u1], [u2], ...], empty: bool }
      if (data && Array.isArray(data.updates)) {
        if (data.updates.length > 0) {
          for (const updateData of data.updates) {
            try {
              const updateArray = new Uint8Array(updateData);
              Y.applyUpdate(this.doc, updateArray, this);
            } catch (err) {
              console.error('Error applying sync update:', err);
            }
          }
        }
        this.serverHadState = !data.empty && data.updates.length > 0;
      } else if (data && data.data && data.data.length > 0) {
        // Legacy fallback: { data: [...] } (single array — kept for backward compat)
        try {
          const updateArray = new Uint8Array(data.data);
          Y.applyUpdate(this.doc, updateArray, this);
          this.serverHadState = true;
        } catch (err) {
          console.error('Error applying initial sync update:', err);
          this.serverHadState = false;
        }
      } else {
        this.serverHadState = false;
      }

      this.synced = true;
      this.emit('synced', { serverHadState: this.serverHadState });
    };
    this.socket.on('sync', this._socketHandlers.sync);

    // Incremental updates from other users
    this._socketHandlers.update = (data) => {
      if (data && data.data) {
        try {
          const updateArray = new Uint8Array(data.data);
          Y.applyUpdate(this.doc, updateArray, this);
        } catch (err) {
          console.error('Error applying update:', err);
        }
      }
    };
    this.socket.on('update', this._socketHandlers.update);

    this._socketHandlers.binary_update = (binaryData) => {
      try {
        const updateArray = new Uint8Array(binaryData);
        Y.applyUpdate(this.doc, updateArray, this);
      } catch (err) {
        console.error('Error applying binary update:', err);
      }
    };
    this.socket.on('binary_update', this._socketHandlers.binary_update);

    // Awareness (cursor positions) from other users
    this._socketHandlers.awareness = (data) => {
      if (data && data.data && data.user_id) {
        try {
          const userId = data.user_id;
          const clientId = data.client_id || this.getConsistentUserId(userId);

          const newState = {
            user: {
              name: data.data.user?.name || 'Unknown',
              color: data.data.user?.color || '#cccccc',
              id: userId,
            },
            // selection must be top-level, not nested under user
            selection: data.data.selection,
          };

          if (clientId !== this.doc.clientID) {
            this.awareness.states.set(clientId, newState);
            this._triggerAwarenessCallbacks();
          }
        } catch (err) {
          console.error('Error handling awareness update:', err);
        }
      }
    };
    this.socket.on('awareness', this._socketHandlers.awareness);

    // User leaving
    this._socketHandlers.user_left = (data) => {
      if (data && data.user_id) {
        try {
          const userId = data.user_id;
          const clientIdsToRemove = [];
          this.awareness.getStates().forEach((state, clientId) => {
            if (state.user && state.user.id === userId) {
              clientIdsToRemove.push(clientId);
            }
          });
          if (clientIdsToRemove.length > 0) {
            this.removeAwarenessStates(clientIdsToRemove, this);
          }
        } catch (err) {
          console.error('Error handling user_left event:', err);
        }
      }
    };
    this.socket.on('user_left', this._socketHandlers.user_left);

    // Reconcile awareness against authoritative user list
    this._socketHandlers.user_status = (data) => {
      if (data && Array.isArray(data.users)) {
        try {
          const currentUserIds = new Set(data.users.map(user => user.id));
          const clientIdsToRemove = [];
          this.awareness.getStates().forEach((state, clientId) => {
            if (state.user && state.user.id && !currentUserIds.has(state.user.id)) {
              clientIdsToRemove.push(clientId);
            }
          });
          if (clientIdsToRemove.length > 0) {
            this.removeAwarenessStates(clientIdsToRemove, this);
          }
        } catch (err) {
          console.error('Error cleaning up awareness in user_status:', err);
        }
      }
    };
    this.socket.on('user_status', this._socketHandlers.user_status);

    // ── Document & awareness listeners (stored for cleanup) ─────────────────

    // Broadcast local document changes to server.
    // Origin check: skip updates that came FROM this provider (received from server)
    // to prevent feedback loops. This follows the standard y-websocket pattern.
    this._docUpdateHandler = (update, origin) => {
      if (origin === this) return;
      try {
        const array = Array.from(update);
        this.socket.emit('update', { data: array });
      } catch (err) {
        console.error('Error sending update:', err);
      }
    };
    this.doc.on('update', this._docUpdateHandler);

    // Broadcast local awareness changes to server
    this._awarenessUpdateHandler = ({ added, updated, removed }) => {
      try {
        const localClientId = this.doc.clientID;
        if ((added && added.includes(localClientId)) || (updated && updated.includes(localClientId))) {
          const state = this.awareness.getLocalState();
          if (state && state.user) {
            this.socket.emit('awareness', {
              data: { user: state.user, selection: state.selection },
              user_id: state.user.id || String(localClientId),
              client_id: localClientId,
            });
            this._triggerAwarenessCallbacks();
          }
        }
        if (removed && removed.length > 0) {
          this._triggerAwarenessCallbacks();
        }
      } catch (err) {
        console.error('Error sending awareness update:', err);
      }
    };
    this.awareness.on('update', this._awarenessUpdateHandler);
  }

  removeAwarenessStates(clientIds, origin) {
    try {
      if (!this.awareness || !Array.isArray(clientIds) || clientIds.length === 0) return;

      const removeSet = new Set(clientIds);
      const states = this.awareness.getStates();
      const newStates = new Map();
      states.forEach((state, clientId) => {
        if (!removeSet.has(clientId)) newStates.set(clientId, state);
      });

      this.awareness.states.clear();
      newStates.forEach((state, clientId) => {
        this.awareness.states.set(clientId, state);
      });

      this.awareness.emit('update', { added: [], updated: [], removed: Array.from(removeSet) });
      this._triggerAwarenessCallbacks();
    } catch (err) {
      console.error('Error removing awareness states:', err);
    }
  }

  setStatus(status) {
    this.status = status;
    this.emit('status', { status });
  }

  disconnect() {
    if (this.socket && this.socket.connected) {
      this.socket.disconnect();
    }
  }

  destroy() {
    // Remove document update listener
    if (this._docUpdateHandler) {
      this.doc.off('update', this._docUpdateHandler);
      this._docUpdateHandler = null;
    }

    // Remove awareness update listener
    if (this._awarenessUpdateHandler) {
      this.awareness.off('update', this._awarenessUpdateHandler);
      this._awarenessUpdateHandler = null;
    }

    // Remove all socket listeners registered in initialize()
    if (this.socket && this._socketHandlers) {
      for (const [event, handler] of Object.entries(this._socketHandlers)) {
        this.socket.off(event, handler);
      }
      this._socketHandlers = {};
    }

    this.disconnect();
    this.callbacks = {};

    if (this.awareness) {
      this.awareness.destroy();
    }
  }
}
